import { useState, useEffect } from 'react';
import { errorHandler, handleNetworkError } from '@/lib/errorHandler';

interface RetryConfig {
  maxAttempts: number;
  baseDelay: number;
  maxDelay: number;
  backoffFactor: number;
  retryCondition?: (error: any) => boolean;
}

interface NetworkRequestConfig {
  timeout?: number;
  retry?: Partial<RetryConfig>;
  signal?: AbortSignal;
}

const defaultRetryConfig: RetryConfig = {
  maxAttempts: 3,
  baseDelay: 1000,
  maxDelay: 10000,
  backoffFactor: 2,
  retryCondition: (error) => {
    // Retry on network errors, 5xx errors, and timeouts
    if (!navigator.onLine) return false; // Don't retry if offline
    
    const status = error?.status || error?.response?.status;
    return (
      !status || // Network error
      status >= 500 || // Server error
      status === 408 || // Request timeout
      status === 429 // Rate limited
    );
  }
};

// Exponential backoff with jitter
const calculateDelay = (attempt: number, config: RetryConfig): number => {
  const exponentialDelay = config.baseDelay * Math.pow(config.backoffFactor, attempt - 1);
  const jitter = Math.random() * 1000; // Add random jitter
  return Math.min(exponentialDelay + jitter, config.maxDelay);
};

// Retry wrapper with exponential backoff
export const withRetry = async <T>(
  operation: () => Promise<T>,
  config: Partial<RetryConfig> = {}
): Promise<T> => {
  const retryConfig = { ...defaultRetryConfig, ...config };
  let lastError: any;
  
  for (let attempt = 1; attempt <= retryConfig.maxAttempts; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      
      // Don't retry if condition is not met
      if (!retryConfig.retryCondition!(error)) {
        throw error;
      }
      
      // Don't retry on last attempt
      if (attempt === retryConfig.maxAttempts) {
        break;
      }
      
      const delay = calculateDelay(attempt, retryConfig);
      console.warn(`Request failed (attempt ${attempt}/${retryConfig.maxAttempts}), retrying in ${delay}ms`, error);
      
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw lastError;
};

// Timeout wrapper
const withTimeout = <T>(promise: Promise<T>, timeoutMs: number): Promise<T> => {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error('Request timeout'));
    }, timeoutMs);
    
    promise
      .then(resolve)
      .catch(reject)
      .finally(() => clearTimeout(timer));
  });
};

// Enhanced fetch with retry and timeout
export const secureFetch = async (
  url: string,
  options: RequestInit & NetworkRequestConfig = {}
): Promise<Response> => {
  const { timeout = 30000, retry, signal, ...fetchOptions } = options;
  
  const operation = async (): Promise<Response> => {
    const controller = new AbortController();
    const combinedSignal = signal || controller.signal;
    
    try {
      const response = await withTimeout(
        fetch(url, {
          ...fetchOptions,
          signal: combinedSignal
        }),
        timeout
      );
      
      // Check if response is ok
      if (!response.ok) {
        const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
        (error as any).status = response.status;
        (error as any).response = response;
        throw error;
      }
      
      return response;
    } catch (error) {
      // Handle different error types
      if (error instanceof TypeError && error.message.includes('fetch')) {
        handleNetworkError(error);
        throw new Error('Network error - please check your connection');
      }
      
      if (error.name === 'AbortError') {
        throw new Error('Request was cancelled');
      }
      
      throw error;
    }
  };
  
  if (retry) {
    return withRetry(operation, retry);
  }
  
  return operation();
};

// Supabase operation wrapper with error handling
export const secureSupabaseOperation = async <T>(
  operation: () => Promise<{ data: T | null; error: any }>,
  context: string
): Promise<T> => {
  try {
    const result = await withRetry(async () => {
      const { data, error } = await operation();
      
      if (error) {
        // Handle Supabase specific errors
        if (error.code === 'PGRST116') {
          throw new Error('No data found');
        }
        
        if (error.code === '42501') {
          throw new Error('Permission denied');
        }
        
        if (error.message?.includes('JWT')) {
          throw new Error('Authentication required');
        }
        
        throw error;
      }
      
      return data;
    });
    
    if (result === null) {
      throw new Error('No data returned from operation');
    }
    
    return result;
  } catch (error) {
    errorHandler.handle(error, { context, operation: 'supabase' });
    throw error;
  }
};

// Network status monitoring
export class NetworkMonitor {
  private listeners: Set<(online: boolean) => void> = new Set();
  private _isOnline: boolean = navigator.onLine;
  
  constructor() {
    window.addEventListener('online', () => {
      this._isOnline = true;
      this.notifyListeners(true);
    });
    
    window.addEventListener('offline', () => {
      this._isOnline = false;
      this.notifyListeners(false);
    });
  }
  
  get isOnline(): boolean {
    return this._isOnline;
  }
  
  addListener(callback: (online: boolean) => void): () => void {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }
  
  private notifyListeners(online: boolean): void {
    this.listeners.forEach(listener => {
      try {
        listener(online);
      } catch (error) {
        console.error('Network status listener error:', error);
      }
    });
  }
}

export const networkMonitor = new NetworkMonitor();

// React hook for network monitoring
export const useNetworkStatus = () => {
  const [isOnline, setIsOnline] = useState(networkMonitor.isOnline);
  
  useEffect(() => {
    return networkMonitor.addListener(setIsOnline);
  }, []);
  
  return isOnline;
};

// Connection quality test
export const testConnectionQuality = async (): Promise<{
  latency: number;
  quality: 'excellent' | 'good' | 'poor' | 'offline';
}> => {
  if (!navigator.onLine) {
    return { latency: Infinity, quality: 'offline' };
  }
  
  try {
    const startTime = performance.now();
    await fetch('data:text/plain;base64,', { method: 'HEAD' });
    const latency = performance.now() - startTime;
    
    let quality: 'excellent' | 'good' | 'poor' | 'offline';
    if (latency < 100) quality = 'excellent';
    else if (latency < 300) quality = 'good';
    else quality = 'poor';
    
    return { latency, quality };
  } catch {
    return { latency: Infinity, quality: 'offline' };
  }
};